import type { ReactNode } from "react";

import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";

import type { ISelectedSerp } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportTypes";
import type { ISerpFeaturesMap } from "app/common/serp/SerpFilterMaps";
import { SERP_MAP } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";

import { SerpIconComponent } from "./SerpStyledComponents";

export interface ISiteSerpFeatures {
    featured: { [serp: string]: Array<{ name: string; color: string }> };
    nonFeatured: string[];
    date: Dayjs;
    sites: Array<{ name: string; color: string }>;
    searchTerm: string;
}

export const buildGroupedSerpFeatures = (
    serpFeatures: string[],
    siteSerpFeatures: Record<string, string[]>,
    sites: Array<{ name: string; color: string }>,
    serpScrapeDate: string,
    searchTerm: string,
    presentLastUpdatedSubtitle = true,
    serpFeaturesMap: ISerpFeaturesMap = SERP_MAP,
): ISiteSerpFeatures => {
    const nonFeatured = [];
    const featured = {};
    // loop over all the serp features
    serpFeatures
        .filter((serp) => Boolean(serpFeaturesMap[serp]))
        .forEach((serp) => {
            let existed = false;
            // loop over the sites features to find which serp feature existed in each site
            sites.forEach(({ name, color }) => {
                const siteFeatures = siteSerpFeatures[name] ?? [];
                if (siteFeatures.includes(serp)) {
                    existed = true;
                    if (!featured[serp]) {
                        featured[serp] = [];
                    }
                    featured[serp].push({ name, color });
                }
            });
            if (!existed) {
                nonFeatured.push(serp);
            }
        });
    return {
        featured,
        nonFeatured,
        sites: sites,
        date: presentLastUpdatedSubtitle ? dayjs.utc(serpScrapeDate) : null,
        searchTerm,
    };
};

export const buildSerpFeaturesIcons = (
    serpFeatures: ISiteSerpFeatures,
    onSerpIconClick?: (serp: ISelectedSerp) => void,
    serpFeaturesMap: ISerpFeaturesMap = SERP_MAP,
    serpFeatureItemBackgroundColor?: string,
): { icons: ReactNode[]; text: string } => {
    const numberOfSerpFeatures = Object.keys(serpFeatures.featured).length + serpFeatures.nonFeatured.length;
    let text = "";
    let icons = Object.keys(serpFeatures.featured)
        .map((serp, index) => {
            return (
                <SerpIconComponent
                    key={`featured-${index}`}
                    iconName={serpFeaturesMap[serp].icon}
                    background={colorsPalettes.sky["200"]}
                    onClick={() => onSerpIconClick?.(serpFeaturesMap[serp] as ISelectedSerp)}
                />
            );
        })
        .concat(
            serpFeatures.nonFeatured.map((serp, index) => {
                return (
                    <SerpIconComponent
                        key={`non-featured-${index}`}
                        iconName={serpFeaturesMap[serp].icon}
                        onClick={() => onSerpIconClick?.(serpFeaturesMap[serp] as ISelectedSerp)}
                        background={serpFeatureItemBackgroundColor}
                    />
                );
            }),
        );
    if (numberOfSerpFeatures > 3) {
        icons = icons.slice(0, 2);
        text = `+${numberOfSerpFeatures - 2}`;
    }
    return {
        icons,
        text,
    };
};
